import { useEffect, useState } from 'react'
import Alert from '@mui/joy/Alert'
import Button from '@mui/joy/Button'
import Card from '@mui/joy/Card'
import CircularProgress from '@mui/joy/CircularProgress'
import FormControl from '@mui/joy/FormControl'
import FormHelperText from '@mui/joy/FormHelperText'
import FormLabel from '@mui/joy/FormLabel'
import Input from '@mui/joy/Input'
import Snackbar from '@mui/joy/Snackbar'
import Stack from '@mui/joy/Stack'
import Switch from '@mui/joy/Switch'
import Typography from '@mui/joy/Typography'
import { LIVE_DAT_MAX_ACTIVE_DAYS, LIVE_DAT_MAX_HORIZON_DAYS } from '@numeo/types-core'
import { liveDatSearchApi, type LiveDatSearchConfigDto, type LiveDatSearchSettings } from 'api/liveDatSearch'
import LiveDatExecutorsCard from './LiveDatExecutorsCard'
import LiveDatUsageCard from './LiveDatUsageCard'

const ACTIVE_DAY_PRESETS = [3, 5, 7]

interface SnackbarState {
    open: boolean
    message: string
    color: 'success' | 'danger'
}

/** Whole number in range, or null: the form keeps what the operator typed, so a half-typed value never saves. */
function parseWhole(raw: string, min: number, max: number): number | null {
    if (!/^\d+$/.test(raw.trim())) return null
    const value = Number(raw)
    return value >= min && value <= max ? value : null
}

function describeChange(before: LiveDatSearchConfigDto, after: LiveDatSearchConfigDto): string[] {
    const lines: string[] = []
    if (before.enabled && !after.enabled) lines.push('Turn OFF live DAT for everyone, Platinum included.')
    if (before.platinumAlwaysLive && !after.platinumAlwaysLive) lines.push('Stop giving Platinum users live DAT.')
    if (after.newUserActiveDays > before.newUserActiveDays)
        lines.push(`Give new users ${after.newUserActiveDays} live-DAT active days (was ${before.newUserActiveDays}).`)
    if (after.newUserLiveSharePercent > before.newUserLiveSharePercent) lines.push(`Raise the live share to ${after.newUserLiveSharePercent}%.`)
    if (after.eligibilityHorizonDays > before.eligibilityHorizonDays) lines.push(`Widen the eligibility horizon to ${after.eligibilityHorizonDays} days.`)
    return lines
}

export default function LiveDatSearchPage() {
    const [settings, setSettings] = useState<LiveDatSearchSettings | null>(null)
    const [enabled, setEnabled] = useState(true)
    const [platinumAlwaysLive, setPlatinumAlwaysLive] = useState(true)
    const [activeDays, setActiveDays] = useState('0')
    const [share, setShare] = useState('100')
    const [horizon, setHorizon] = useState('30')
    const [busy, setBusy] = useState(false)
    const [snackbar, setSnackbar] = useState<SnackbarState>({ open: false, message: '', color: 'success' })

    function applyToForm(config: LiveDatSearchConfigDto, saved: boolean, suggestedActiveDays: number) {
        setEnabled(config.enabled)
        setPlatinumAlwaysLive(config.platinumAlwaysLive)
        // Nothing saved yet: pre-fill the suggested default instead of 0, so a first Save does not leave the new-user rule off.
        setActiveDays(String(saved ? config.newUserActiveDays : suggestedActiveDays))
        setShare(String(config.newUserLiveSharePercent))
        setHorizon(String(config.eligibilityHorizonDays))
    }

    useEffect(() => {
        liveDatSearchApi
            .get()
            .then((next) => {
                setSettings(next)
                applyToForm(next.config, next.saved, next.suggestedActiveDays)
            })
            .catch(() => setSnackbar({ open: true, message: 'Failed to load live DAT settings', color: 'danger' }))
    }, [])

    if (!settings) {
        return (
            <Stack alignItems="center" sx={{ p: 6 }}>
                <CircularProgress />
            </Stack>
        )
    }

    const days = parseWhole(activeDays, 0, LIVE_DAT_MAX_ACTIVE_DAYS)
    const sharePercent = parseWhole(share, 0, 100)
    const horizonDays = parseWhole(horizon, 1, LIVE_DAT_MAX_HORIZON_DAYS)
    const valid = days !== null && sharePercent !== null && horizonDays !== null

    async function save(startNewCohort = false) {
        if (!settings || days === null || sharePercent === null || horizonDays === null) return
        const next: LiveDatSearchConfigDto = {
            ...settings.config,
            ...(startNewCohort ? { newUsersFrom: new Date().toISOString() } : {}),
            enabled,
            platinumAlwaysLive,
            newUserActiveDays: days,
            newUserLiveSharePercent: sharePercent,
            eligibilityHorizonDays: horizonDays,
        }
        const changes = describeChange(settings.config, next)
        if (startNewCohort)
            changes.unshift('Start a new cohort now: only users who sign up from this moment on are eligible, and the start can never move back.')
        if (changes.length > 0 && !window.confirm(`This takes effect for every user within a minute:\n\n- ${changes.join('\n- ')}\n\nSave?`)) return
        setBusy(true)
        try {
            const saved = await liveDatSearchApi.save(next)
            setSettings({ ...settings, config: saved, saved: true })
            applyToForm(saved, true, settings.suggestedActiveDays)
            setSnackbar({ open: true, message: 'Saved', color: 'success' })
        } catch (e) {
            const message = (e as { response?: { data?: { message?: string } } }).response?.data?.message ?? (e instanceof Error ? e.message : 'Save failed')
            setSnackbar({ open: true, message, color: 'danger' })
        } finally {
            setBusy(false)
        }
    }

    const cohortStart = settings.config.newUsersFrom ? new Date(settings.config.newUsersFrom).toLocaleString() : null

    return (
        <Stack spacing={2} sx={{ p: 3, maxWidth: 760 }}>
            <Typography level="h3">Live DAT Search</Typography>
            <Typography level="body-sm">
                Mobile searches are 1st class (always live DAT, DB rows as the fallback) or 2nd class (Smart Search). New users get live DAT for their first
                active days, and Platinum keeps it. A day is active when the user runs a search; the count only moves on days they search.
            </Typography>

            {!settings.saved && (
                <Alert color="neutral" variant="soft">
                    Nothing has been saved yet, so mobile search behaves as it always did: Platinum is live and the new-user rule is off.
                </Alert>
            )}
            {settings.rolloutMode !== 'on' && (
                <Alert color="warning" variant="soft">
                    The default rollout mode is <b>{settings.rolloutMode}</b>, so the master switch, the Platinum toggle and the new-user rule below do not
                    apply yet (except to accounts a Flipt segment has switched to <code>on</code>): the old Platinum plan flag still decides, and in shadow mode
                    these settings are only measured. To stop live DAT in an incident, use the
                    <code> smart-search-dat-fallback-off</code> kill switch in Flipt.
                </Alert>
            )}
            {settings.rolloutMode === 'on' && !enabled && (
                <Alert color="danger" variant="soft">
                    The master switch puts everyone in 2nd class, Platinum included. Use it for a pool-wide incident. To stop only the new-user rule, set its
                    active days to 0.
                </Alert>
            )}

            <Card variant="outlined">
                <Stack spacing={2}>
                    <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                        <div>
                            <FormLabel>Live DAT classes enabled</FormLabel>
                            <FormHelperText>Master switch for the whole model.</FormHelperText>
                        </div>
                        <Switch checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
                    </FormControl>

                    <FormControl orientation="horizontal" sx={{ justifyContent: 'space-between' }}>
                        <div>
                            <FormLabel>Platinum always gets live DAT</FormLabel>
                            <FormHelperText>Off: Platinum users search like everyone else.</FormHelperText>
                        </div>
                        <Switch checked={platinumAlwaysLive} onChange={(e) => setPlatinumAlwaysLive(e.target.checked)} />
                    </FormControl>

                    <FormControl error={days === null}>
                        <FormLabel>New-user live-DAT active days</FormLabel>
                        <Stack direction="row" spacing={1} alignItems="center">
                            <Input
                                value={activeDays}
                                onChange={(e) => setActiveDays(e.target.value)}
                                sx={{ width: 100 }}
                                slotProps={{ input: { inputMode: 'numeric' } }}
                            />
                            {ACTIVE_DAY_PRESETS.map((preset) => (
                                <Button key={preset} size="sm" variant={days === preset ? 'solid' : 'outlined'} onClick={() => setActiveDays(String(preset))}>
                                    {preset}
                                </Button>
                            ))}
                            <Button size="sm" variant={days === 0 ? 'solid' : 'outlined'} color="neutral" onClick={() => setActiveDays('0')}>
                                Off
                            </Button>
                        </Stack>
                        <FormHelperText>
                            {days === null
                                ? `A whole number from 0 to ${LIVE_DAT_MAX_ACTIVE_DAYS}.`
                                : `0 turns the new-user rule off. Suggested: ${settings.suggestedActiveDays}.`}
                        </FormHelperText>
                    </FormControl>

                    <FormControl error={sharePercent === null}>
                        <FormLabel>Share of new users who get live DAT (%)</FormLabel>
                        <Input value={share} onChange={(e) => setShare(e.target.value)} sx={{ width: 100 }} slotProps={{ input: { inputMode: 'numeric' } }} />
                        <FormHelperText>
                            {sharePercent === null
                                ? 'A whole number from 0 to 100.'
                                : 'The rest are the A/B control and search as 2nd class. Change it between tests, not during one: it moves users between arms.'}
                        </FormHelperText>
                    </FormControl>

                    <FormControl error={horizonDays === null}>
                        <FormLabel>Eligibility horizon (days from signup)</FormLabel>
                        <Input
                            value={horizon}
                            onChange={(e) => setHorizon(e.target.value)}
                            sx={{ width: 100 }}
                            slotProps={{ input: { inputMode: 'numeric' } }}
                        />
                        <FormHelperText>
                            {horizonDays === null
                                ? `A whole number from 1 to ${LIVE_DAT_MAX_HORIZON_DAYS}.`
                                : 'A new user must use their active days within this long after signing up.'}
                        </FormHelperText>
                    </FormControl>

                    <FormControl>
                        <FormLabel>Cohort start</FormLabel>
                        <Typography level="body-sm">
                            {cohortStart
                                ? `Users who signed up on or after ${cohortStart} are eligible.`
                                : 'Set automatically when the new-user rule is first switched on.'}
                        </Typography>
                        <FormHelperText>
                            It only moves forward, so existing users are never swept in. Move it to now before switching a rollout on.
                        </FormHelperText>
                        <Button
                            size="sm"
                            variant="outlined"
                            sx={{ alignSelf: 'flex-start', mt: 1 }}
                            loading={busy}
                            disabled={!valid}
                            onClick={() => void save(true)}
                        >
                            Start a new cohort now
                        </Button>
                    </FormControl>

                    <Stack direction="row" spacing={2} alignItems="center">
                        <Button loading={busy} disabled={!valid} onClick={() => void save(false)}>
                            Save
                        </Button>
                    </Stack>
                </Stack>
            </Card>

            <LiveDatUsageCard />
            <LiveDatExecutorsCard />

            <Snackbar
                open={snackbar.open}
                color={snackbar.color}
                variant="soft"
                autoHideDuration={4000}
                onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
            >
                {snackbar.message}
            </Snackbar>
        </Stack>
    )
}
